import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { useLeadModal } from "@/context/LeadModalContext";
import { scrollToId } from "@/hooks/useLenis";
import { MaskedLines } from "./Reveal";
import { media, microBenefits } from "@/data/site";
import { heroBullets } from "@/data/numbers";

export const Hero = () => {
  const { openModal } = useLeadModal();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBig = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="top" ref={ref} className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-24 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          {/* Left — copy */}
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="font-sans-ui text-[10px] sm:text-[11px] tracking-mega text-ink/50 mb-7"
            >
              FASHION INFRASTRUCTURE&nbsp;&nbsp;•&nbsp;&nbsp;PRIVATE LABEL&nbsp;&nbsp;•&nbsp;&nbsp;FULFILLMENT
            </motion.div>

            <h1 data-testid="hero-title" className="font-display font-light text-ink leading-[1.12] tracking-tight text-4xl sm:text-5xl lg:text-[4rem]">
              <MaskedLines
                lines={["Crea il tuo brand di moda.", "Scegli tra migliaia di capi.", "Noi li prepariamo e spediamo con il tuo brand."]}
                lineClassName=""
                delay={0.15}
                mount
              />
            </h1>

            <div className="hero-rise">
              <ul data-testid="hero-bullets" className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 font-sans-ui text-[11px] sm:text-xs tracking-mega text-ink font-medium">
                {heroBullets.map((b, i) => (
                  <li key={b} className="flex items-center gap-3">
                    {i > 0 && <span className="text-goldtan">·</span>}
                    {b.toUpperCase()}
                  </li>
                ))}
              </ul>

              <p className="mt-8 max-w-xl font-sans-ui text-sm sm:text-base text-ink/70 leading-relaxed">
                Costruisci la tua collezione scegliendo liberamente tra migliaia di capi del catalogo My Brand Online.
                Non devi acquistare stock in anticipo: scegli cosa vuoi vendere, pubblicalo sul tuo store e lascia a noi
                la gestione della merce e del magazzino.
              </p>
              <p className="mt-4 max-w-xl font-sans-ui text-sm sm:text-base text-ink/70 leading-relaxed">
                Quando arriva un ordine, prendiamo il prodotto acquistato, lo prepariamo con l'identità del tuo
                brand — etichetta, cartellino e packaging — e lo spediamo direttamente al tuo cliente.
              </p>

              <p className="mt-8 font-display italic text-2xl sm:text-3xl text-ink leading-snug">
                Tu scegli. Tu vendi.<br className="hidden sm:block" /> Noi facciamo tutto il resto.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4 sm:gap-6">
                <button
                  data-testid="hero-primary-cta"
                  onClick={openModal}
                  className="group inline-flex items-center gap-3 bg-ink text-ivory px-9 py-4 text-[11px] tracking-mega font-medium hover:bg-charcoal transition-colors duration-300"
                >
                  RICHIEDI ACCESSO
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
                <button
                  data-testid="hero-secondary-cta"
                  onClick={() => scrollToId("#come-funziona")}
                  className="inline-flex items-center gap-2 text-[11px] tracking-mega font-medium text-ink/70 hover:text-ink border-b border-ink/30 hover:border-ink pb-1 transition-colors duration-300"
                >
                  SCOPRI COME FUNZIONA
                </button>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 font-sans-ui text-[11px] sm:text-xs tracking-wide text-ink/55">
                {microBenefits.map((b, i) => (
                  <span key={b} className="flex items-center gap-4">
                    {i > 0 && <span className="text-ink/25">•</span>}
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right — visual */}
          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-[420px] sm:h-[560px] lg:h-[660px]"
          >
            <motion.div style={{ y: yBig }} className="absolute right-0 top-0 w-[72%] h-[80%] overflow-hidden bg-linen">
              <img src={media.heroComposition} alt="Capi moda donna con etichetta, cartellino e packaging YOUR BRAND" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div style={{ y: ySmall }} className="absolute left-0 bottom-0 w-[52%] h-[58%] overflow-hidden bg-linen shadow-2xl">
              <img src={media.heroModel} alt="Modella con capo e packaging brandizzato" className="w-full h-full object-cover" />
            </motion.div>
            <motion.div
              style={{ y: ySmall }}
              className="absolute left-2 sm:left-4 bottom-4 sm:bottom-6 bg-ivory px-4 py-3 border hairline"
            >
              <p className="font-sans-ui text-[9px] tracking-mega text-ink/50">SCEGLI · VENDI · BRANDIZZIAMO · SPEDIAMO</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
